/**
 * Presentation templates endpoint helpers — chrome / paper / writing_style.
 *
 * BE source of truth:
 *   ``ando-agent/src/api/v1/endpoints/presentation_templates.py``
 *   prefix ``/presentation-templates``. Three kinds in one router:
 *     - ``chrome``         (ChromeSpec)
 *     - ``paper``          (PaperSpec)
 *     - ``writing_style``  (WritingStyleSpec)
 *
 *   12 built-ins shipped (4 chrome × 4 paper × 4 voice) + user/org custom
 *   templates persisted in ``custom_chrome_templates``. Resolution chain
 *   (BE-side, 6 steps):
 *     1. user-custom slug match
 *     2. org-custom slug match
 *     3. built-in slug match
 *     4. user preference (``user_preferences.metadata``)
 *     5. org preference (``organizations.metadata``)
 *     6. ``editorial_ando`` fallback
 *
 *   Specs are stored as opaque JSON blobs on the wire; the validator runs
 *   BE-side (``_validate_spec``) — FE only renders schema-driven editors.
 *
 * Wire contract is FROZEN:
 *   - Built-ins have ``slug === null`` and ``scope === "builtin"``.
 *   - Only user/org-custom rows accept PATCH / DELETE.
 *   - ``preview_url`` is a BE-proxied PNG path (``/api/v1/presentation-templates/_preview/<key>``).
 */

import { apiClient } from "../ApiClient.js";

/** The three template kinds shipped today; adding a fourth = single Literal entry BE-side. */
export type TemplateKind = "chrome" | "paper" | "writing_style";

/**
 * Where a template lives. ``builtin`` rows are read-only registry entries;
 * ``user`` / ``org`` rows are persisted in the custom-template tables;
 * ``public`` rows are org templates flagged ``is_public=true``.
 */
export type TemplateScope = "builtin" | "user" | "org" | "public";

/** Compact list-view template descriptor (matches ``TemplateSummary`` BE). */
export interface TemplateSummary {
  name: string;
  description: string;
  kind: TemplateKind;
  scope: TemplateScope;
  /** ``null`` for built-ins; non-empty slug for persisted user/org rows. */
  slug: string | null;
  preview_url: string | null;
}

/** Detail view (matches ``TemplateDetail`` BE) — includes the validated spec JSON. */
export interface TemplateDetail extends TemplateSummary {
  /** Opaque JSON; shape per ``kind`` — ChromeSpec | PaperSpec | WritingStyleSpec. */
  spec: Record<string, unknown>;
}

/** Body for ``POST /presentation-templates`` (new custom row). */
export interface CreateTemplateBody {
  name: string;
  description: string;
  kind: TemplateKind;
  spec: Record<string, unknown>;
}

/** Body for ``PATCH /presentation-templates/{slug}`` (partial update). */
export interface UpdateTemplateBody {
  name?: string;
  description?: string;
  spec?: Record<string, unknown>;
  is_public?: boolean;
}

/** Resolved preferences for the current user (matches ``PreferencesResponse`` BE). */
export interface PreferencesResponse {
  /** User-level override on ``user_preferences.metadata``, ``null`` if unset. */
  user_default: string | null;
  /** Org-level default on ``organizations.metadata``, ``null`` if unset. */
  org_default: string | null;
  /** The slug the resolution chain picked — never ``null`` (BE falls back to ``editorial_ando``). */
  effective: string;
}

/** Body for ``PATCH /presentation-templates/_preferences``. */
export interface SetPreferenceBody {
  template_name: string;
}

// =============================================================================
// Fetchers
// =============================================================================

/**
 * List built-in + user-custom + org-custom templates.
 *
 * Optional ``kind`` filter narrows the response to a single kind. Without it
 * the BE returns all three kinds in one array — callers downstream filter by
 * ``kind`` to drive per-kind UIs.
 */
export async function listPresentationTemplates(kind?: TemplateKind): Promise<TemplateSummary[]> {
  const qs = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  return apiClient.get<TemplateSummary[]>(`/presentation-templates${qs}`);
}

/** Detail view (incl. spec JSON) of a single template by slug. */
export async function getTemplate(slug: string): Promise<TemplateDetail> {
  return apiClient.get<TemplateDetail>(`/presentation-templates/${encodeURIComponent(slug)}`);
}

/** Create a custom template (scope: ``user``). */
export async function createTemplate(body: CreateTemplateBody): Promise<TemplateDetail> {
  return apiClient.post<TemplateDetail>("/presentation-templates", body);
}

/** Partial update — only ``user`` / ``org`` scope rows accept this (built-ins are read-only). */
export async function updateTemplate(
  slug: string,
  body: UpdateTemplateBody,
): Promise<TemplateDetail> {
  return apiClient.patch<TemplateDetail>(
    `/presentation-templates/${encodeURIComponent(slug)}`,
    body,
  );
}

/** Delete a custom template — 204 on success; rejects on built-in slugs BE-side. */
export async function deleteTemplate(slug: string): Promise<void> {
  await apiClient.delete<void>(`/presentation-templates/${encodeURIComponent(slug)}`);
}

/** Read the resolved user-level + org-level template preferences. */
export async function getTemplatePreferences(): Promise<PreferencesResponse> {
  return apiClient.get<PreferencesResponse>("/presentation-templates/_preferences");
}

/** Persist a user-level template preference; 204 on success. */
export async function setTemplatePreference(body: SetPreferenceBody): Promise<void> {
  await apiClient.patch<void>("/presentation-templates/_preferences", body);
}

/**
 * Infer a ``WritingStyleSpec`` from a corpus of past writings the user pastes.
 *
 * BE endpoint: ``POST /presentation-templates/_infer-writing-style``. Returns
 * a ready-to-save ``TemplateDetail`` (kind=``writing_style``, scope=``user``)
 * the FE can then ``PATCH`` to refine or persist as-is.
 */
export async function inferWritingStyleTemplate(samples: string[]): Promise<TemplateDetail> {
  return apiClient.post<TemplateDetail>(
    "/presentation-templates/_infer-writing-style",
    { samples },
  );
}
